import { BaseEntity } from '../../constants';
import {
  InputsTypes,
  QuestionTemplate,
} from '../../components/IntakeForms/intakeFormTypes';
import { UserProfile } from '../../utils/UserUtils';

export interface IntakeFormFields {
  name?: string;
  latestResponse: string;
}

export interface InputEntity extends BaseEntity {
  fields: {
    questions: IntakeInputModel[];
  };
}

export interface IntakeFormInputTemplate {
  id: string;
  fields: QuestionTemplate;
}
export interface IntakeFormAdditionalFields {
  formResponseRequests: number;
  formResponsesCompleted: number;
  inputTemplates: IntakeFormInputTemplate[];
}

export interface IntakeForm extends BaseEntity {
  fields: IntakeFormFields;
  additionalFields?: IntakeFormAdditionalFields;
}

export interface IntakeInputModel {
  inputType: InputsTypes | null;
  title: string | null;
  inputId: string;
  responseValue: any | null;
  responseValueData: any | null;
  testDataFileSizes: number[] | null;
  options: string[];
}

export interface IntakeFormResponse extends BaseEntity {
  fields: {
    questions: IntakeInputModel[];
  };
  additionalFields?: {
    formName: string;
  };
}

export interface FormCreatePanelType {
  data: QuestionTemplate | null;
}

export interface FormTableRowModel {
  formId: string;
  ref: string;
  responseId: string | null;
  name: string;
  responseCount: {
    responded: number;
    requested: number;
  };
  status: string;
  creator: UserProfile | null;
  sender: UserProfile | null;
  formFullData: IntakeForm;
  created?: string;
  notificationCount: number;
  waitForResponse?: number;
  latestResponse?: string;
}

export interface IntakeFormsState {
  items: IntakeForm[];
  isFormLoading: boolean;
  isFormLoaded: boolean;
  isResponseLoading: boolean;
  isResponseLoaded: boolean;
  selectedResponseId: string;
  selectedResponseClientName: string;
  responses: IntakeFormResponse[] | null;
  selectedUserResponses: IntakeFormResponse[] | null;
  formCreatePanel: FormCreatePanelType;
}

// Response Actions
export const SET_RESPONSE_STATE_REQUEST = 'SET_RESPONSE_STATE_REQUEST';
export const SET_RESPONSE_STATE_ADDED = 'SET_RESPONSE_STATE_ADDED';
export const SET_RESPONSE_STATE_FAIL = 'SET_RESPONSE_STATE_FAIL';
export const SET_RESPONSE_STATE_UPDATED = 'SET_RESPONSE_STATE_UPDATED';
export const SET_USER_RESPONSE_STATE_REQUEST =
  'SET_USER_RESPONSE_STATE_REQUEST';
export const SET_USER_RESPONSE_STATE_ADDED = 'SET_USER_RESPONSE_STATE_ADDED';
export const SET_USER_RESPONSE_STATE_FAIL = 'SET_USER_RESPONSE_STATE_FAIL';
export const DELETE_RESPONSES_SUCCESS = 'DELETE_RESPONSES_SUCCESS';

// Form Actions
export const SET_FORM_STATE_REQUEST = 'SET_FORM_STATE_REQUEST';
export const INSERT_INTAKE_FORMS = 'INSERT_INTAKE_FORMS';
export const SET_FORM_STATE_UPDATED = 'SET_FORM_STATE_UPDATED';
export const UPDATE_INTAKE_FORMS = 'UPDATE_INTAKE_FORMS';

export const SET_RESPONSE_PAGE_STATE = 'SET_RESPONSE_PAGE_STATE';
export const SET_FORM_CREATE_PANEL = 'SET_FORM_CREATE_PANEL';

export const DELETE_INTAKE_FORM_FAIL = 'DELETE_INTAKE_FORM_FAIL';
export const DELETE_INTAKE_FORM_SUCCESS = 'DELETE_INTAKE_FORM_SUCCESS';

interface FormRequestAction {
  type: typeof SET_FORM_STATE_REQUEST;
}

interface FormAddedAction {
  type: typeof INSERT_INTAKE_FORMS;
  payload: IntakeForm[];
}

interface FormUpdatedAction {
  type: typeof SET_FORM_STATE_UPDATED;
  payload: IntakeForm;
}

interface ResponseRequestAction {
  type: typeof SET_RESPONSE_STATE_REQUEST;
}

interface ResponseFailAction {
  type: typeof SET_RESPONSE_STATE_FAIL;
  error: string;
}

interface ResponseAddedAction {
  type: typeof SET_RESPONSE_STATE_ADDED;
  payload: IntakeFormResponse[];
}

interface ResponseUpdatedAction {
  type: typeof SET_RESPONSE_STATE_UPDATED;
  payload: IntakeFormResponse;
}

export interface ResponsePageState {
  selectedResponseId?: string;
  selectedResponseClientName?: string;
  responses?: IntakeFormResponse[];
}
interface SetResponsePageStateAction {
  type: typeof SET_RESPONSE_PAGE_STATE;
  payload: ResponsePageState;
}

interface SetFormCreatePanel {
  type: typeof SET_FORM_CREATE_PANEL;
  payload: FormCreatePanelType;
}

interface UserResponseRequestAction {
  type: typeof SET_USER_RESPONSE_STATE_REQUEST;
}

interface UserResponseFailAction {
  type: typeof SET_USER_RESPONSE_STATE_FAIL;
  error: string;
}

interface UserResponseAddedAction {
  type: typeof SET_USER_RESPONSE_STATE_ADDED;
  payload: IntakeFormResponse[];
}
interface DeleteIntakeFormFailAction {
  type: typeof DELETE_INTAKE_FORM_FAIL;
  error: string;
}

interface DeleteIntakeFormSuccessAction {
  type: typeof DELETE_INTAKE_FORM_SUCCESS;
  payload: string[];
}

interface UpdateIntakeFormsActionType {
  type: typeof UPDATE_INTAKE_FORMS;
  payload: IntakeForm[];
}

interface DeleteFormResponseActionType {
  type: typeof DELETE_RESPONSES_SUCCESS;
  payload: string[];
}

export type IntakeFormsActionTypes =
  | FormRequestAction
  | FormAddedAction
  | FormUpdatedAction
  | ResponseRequestAction
  | ResponseAddedAction
  | ResponseUpdatedAction
  | ResponseFailAction
  | SetResponsePageStateAction
  | SetFormCreatePanel
  | UserResponseRequestAction
  | UserResponseFailAction
  | UserResponseAddedAction
  | DeleteIntakeFormFailAction
  | DeleteIntakeFormSuccessAction
  | UpdateIntakeFormsActionType
  | DeleteFormResponseActionType;
